import React, { useState } from "react";
import airports from "../data/airports.json";
import airlinesData from "../data/airlines.json";
import { motion, AnimatePresence } from "framer-motion";

function AirportAutocomplete({ label, value, onChange, placeholder }) {
  const [query, setQuery] = useState("");
  const [showList, setShowList] = useState(false);

  // Filtrer les aéroports selon la saisie
  const filtered = airports.filter(a =>
    (a.name + " " + a.city + " " + a.iata_code)
      .toLowerCase()
      .includes(query.toLowerCase())
  ).slice(0, 8);

  const handleInput = (e) => {
    setQuery(e.target.value);
    setShowList(true);
    onChange(""); // Réinitialise le code IATA si l'utilisateur modifie
  };

  const handleSelect = (airport) => {
    setQuery(`${airport.city} - ${airport.name} (${airport.iata_code})`);
    onChange(airport.iata_code);
    setShowList(false);
  };

  return (
    <div className="relative">
      <label className="font-medium text-gray-700">{label}</label>
      <input
        type="text"
        value={query}
        onChange={handleInput}
        onFocus={() => setShowList(true)}
        onBlur={() => setTimeout(() => setShowList(false), 150)}
        placeholder={placeholder}
        className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
        autoComplete="off"
      />
      <AnimatePresence>
        {showList && filtered.length > 0 && (
          <motion.ul
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="absolute z-10 bg-white border rounded-lg shadow-lg mt-1 w-full max-h-64 overflow-auto"
          >
            {filtered.map((a) => (
              <li
                key={a.iata_code}
                onMouseDown={() => handleSelect(a)}
                className="px-4 py-2 cursor-pointer hover:bg-blue-50 flex items-center gap-2"
              >
                <span className="font-bold text-blue-700">{a.iata_code}</span>
                <span>{a.city} - {a.name}</span>
                <span className="ml-auto text-xs text-gray-400">{a.country}</span>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}

const FlightSearchForm = ({ onSearch }) => {
  const [form, setForm] = useState({
    originLocationCode: "",
    destinationLocationCode: "",
    departureDate: "",
    returnDate: "",
    adults: 1,
    children: 0,
    infants: 0,
    travelClass: "ECONOMY",
    nonStop: false,
    currencyCode: "XOF",
    includedAirlineCodes: [], // Nouvelles compagnies sélectionnées
  });

  const [showForm, setShowForm] = useState(false);
  const [reduced, setReduced] = useState(false);

  const handleOpenVoiceAssistant = () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('open-voice-assistant'));
    }
  };

  // Compagnie favorite - Air Côte d'Ivoire
  const favoriteAirline = { code: "HF", name: "Air Côte d'Ivoire" };

  // Compagnies d'Afrique de l'Ouest
  const westAfricanAirlines = [
    { code: "HC", name: "Air Senegal" },
    { code: "2J", name: "Air Burkina" },
    { code: "KP", name: "ASKY Airlines" },
    { code: "L6", name: "Mauritania Airlines" },
    { code: "C2", name: "Ceiba Intercontinental" },
    { code: "QC", name: "Camair-Co" },
  ];

  // Compagnies leaders en Afrique
  const africanLeaderAirlines = [
    { code: "ET", name: "Ethiopian Airlines" },
    { code: "KQ", name: "Kenya Airways" },
    { code: "MS", name: "EgyptAir" },
    { code: "AT", name: "Royal Air Maroc" },
    { code: "AH", name: "Air Algérie" },
    { code: "TU", name: "Tunis Air" },
    { code: "SA", name: "South African Airways" },
    { code: "DT", name: "TAAG Angola Airlines" },
    { code: "ME", name: "Middle East Airlines" },
  ];

  // Compagnies internationales populaires
  const internationalAirlines = airlinesData.data
    .filter(airline => 
      airline.iata_code && 
      airline.iata_code.length > 0 &&
      !westAfricanAirlines.find(a => a.code === airline.iata_code) &&
      !africanLeaderAirlines.find(a => a.code === airline.iata_code) &&
      airline.iata_code !== favoriteAirline.code
    )
    .sort((a, b) => (b.num_aircraft || 0) - (a.num_aircraft || 0))
    .slice(0, 20)
    .map(a => ({ code: a.iata_code, name: a.name }));

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({
      ...form,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // Gestion de la sélection des compagnies aériennes
  const handleAirlineToggle = (code) => {
    const current = form.includedAirlineCodes;
    if (current.includes(code)) {
      // Retirer la compagnie
      setForm({
        ...form,
        includedAirlineCodes: current.filter(c => c !== code)
      });
    } else {
      // Ajouter la compagnie (max 3)
      if (current.length < 3) {
        setForm({
          ...form,
          includedAirlineCodes: [...current, code]
        });
      }
    }
  };

  // Pour les autocompleteurs
  const handleOrigin = (iata) => setForm({ ...form, originLocationCode: iata });
  const handleDestination = (iata) => setForm({ ...form, destinationLocationCode: iata });

  const handleSubmit = (e) => {
    e.preventDefault();
    setReduced(true);
    
    // Préparer les données pour l'envoi
    const searchData = {
      ...form,
      // Convertir le tableau de codes en chaîne CSV pour l'API Amadeus
      includedAirlineCodes: form.includedAirlineCodes.length > 0 
        ? form.includedAirlineCodes.join(',') 
        : undefined
    };
    
    onSearch(searchData);
    setTimeout(() => {
      setReduced(false);
      setShowForm(false);
    }, 3000);
  };

  const handleFlyerClick = () => {
    setShowForm(true);
    setReduced(false);
  };

  const handleClose = () => {
    setShowForm(false);
    setReduced(false);
  };

  return (
    <>
      {/* Bouton flottant */}
      {!showForm && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 z-30">
          <motion.button
            onClick={handleFlyerClick}
            className="bg-gradient-to-r from-blue-500 to-cyan-400 text-white rounded-2xl px-6 py-3 text-base sm:text-lg font-semibold flex items-center gap-2 shadow-lg"
          >
            🔎 Rechercher un vol
          </motion.button>

          <motion.button
            type="button"
            onClick={handleOpenVoiceAssistant}
            className="bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white rounded-2xl px-4 sm:px-5 py-3 text-base sm:text-lg font-semibold flex items-center gap-2 shadow-lg"
          >
            <span className="text-lg">🎤</span>
            <span className="hidden sm:inline">Parler à l’IA</span>
          </motion.button>
        </div>
      )}

      {/* Overlay + Form */}
      <AnimatePresence>
        {showForm && (
          <>
            {/* Overlay */}
            <motion.div
              key="overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={handleClose}
              className="fixed inset-0 bg-black/30 z-40"
            />

            {/* Formulaire centré */}
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              {!reduced ? (
                <motion.form
                  key="form"
                  onSubmit={handleSubmit}
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 50, opacity: 0 }}
                  transition={{ type: "spring", damping: 20 }}
                  className="w-11/12 max-w-lg bg-white rounded-2xl shadow-xl p-6 sm:p-10 flex flex-col gap-4 max-h-[80vh] overflow-y-auto relative"
                >
                  {/* Bouton fermer */}
                  <button
                    type="button"
                    onClick={handleClose}
                    className="absolute top-3 right-4 text-blue-500 text-2xl hover:text-red-500"
                  >
                    ×
                  </button>

                  {/* Origine autocomplete */}
                  <AirportAutocomplete
                    label="Origine"
                    value={form.originLocationCode}
                    onChange={handleOrigin}
                    placeholder="Ville, aéroport ou code IATA"
                  />

                  {/* Destination autocomplete */}
                  <AirportAutocomplete
                    label="Destination"
                    value={form.destinationLocationCode}
                    onChange={handleDestination}
                    placeholder="Ville, aéroport ou code IATA"
                  />

                  {/* Dates */}
                  <div>
                    <label className="font-medium text-gray-700">Date de départ</label>
                    <input
                      type="date"
                      name="departureDate"
                      value={form.departureDate}
                      onChange={handleChange}
                      required
                      className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                  </div>
                  <div>
                    <label className="font-medium text-gray-700">Date de retour (facultatif)</label>
                    <input
                      type="date"
                      name="returnDate"
                      value={form.returnDate}
                      onChange={handleChange}
                      className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                  </div>

                  {/* Passagers */}
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <label className="font-medium text-gray-700">Adultes</label>
                      <input
                        type="number"
                        name="adults"
                        value={form.adults}
                        onChange={handleChange}
                        min="1"
                        required
                        className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="font-medium text-gray-700">Enfants</label>
                      <input
                        type="number"
                        name="children"
                        value={form.children}
                        onChange={handleChange}
                        min="0"
                        className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="font-medium text-gray-700">Bébés</label>
                      <input
                        type="number"
                        name="infants"
                        value={form.infants}
                        onChange={handleChange}
                        min="0"
                        className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                      />
                    </div>
                  </div>

                  {/* Classe */}
                  <div>
                    <label className="font-medium text-gray-700">Classe</label>
                    <select
                      name="travelClass"
                      value={form.travelClass}
                      onChange={handleChange}
                      className="mt-1 w-full px-3 py-2 border rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    >
                      <option value="ECONOMY">Économie</option>
                      <option value="PREMIUM_ECONOMY">Premium Économie</option>
                      <option value="BUSINESS">Business</option>
                      <option value="FIRST">Première</option>
                    </select>
                  </div>

                  {/* Sélection des compagnies aériennes */}
                  <div>
                    <label className="font-medium text-gray-700 mb-2 block">
                      Compagnies aériennes (max 3)
                      {form.includedAirlineCodes.length > 0 && (
                        <span className="text-sm text-blue-600 ml-2">
                          ({form.includedAirlineCodes.length} sélectionnée{form.includedAirlineCodes.length > 1 ? 's' : ''})
                        </span>
                      )}
                    </label>
                    <div className="max-h-96 overflow-y-auto border rounded-lg p-3 bg-gray-50">
                      
                      {/* Compagnie Favorite - Air Côte d'Ivoire */}
                      <div className="mb-4">
                        <h3 className="text-xs font-bold text-orange-700 mb-2 flex items-center gap-1">
                          ⭐ COMPAGNIE FAVORITE
                        </h3>
                        <button
                          type="button"
                          onClick={() => handleAirlineToggle(favoriteAirline.code)}
                          disabled={
                            !form.includedAirlineCodes.includes(favoriteAirline.code) &&
                            form.includedAirlineCodes.length >= 3
                          }
                          className={`w-full text-left px-4 py-3 rounded-lg text-sm font-semibold transition shadow-md ${
                            form.includedAirlineCodes.includes(favoriteAirline.code)
                              ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white scale-105'
                              : form.includedAirlineCodes.length >= 3
                              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                              : 'bg-gradient-to-r from-orange-400 to-orange-500 text-white hover:from-orange-500 hover:to-orange-600'
                          }`}
                        >
                          <span className="font-extrabold text-base">{favoriteAirline.code}</span> - {favoriteAirline.name} ✈️
                        </button>
                      </div>

                      {/* Compagnies d'Afrique de l'Ouest */}
                      <div className="mb-4">
                        <h3 className="text-xs font-bold text-green-700 mb-2">
                          🌍 AFRIQUE DE L'OUEST
                        </h3>
                        <div className="grid grid-cols-2 gap-2">
                          {westAfricanAirlines.map((airline) => (
                            <button
                              key={airline.code}
                              type="button"
                              onClick={() => handleAirlineToggle(airline.code)}
                              disabled={
                                !form.includedAirlineCodes.includes(airline.code) &&
                                form.includedAirlineCodes.length >= 3
                              }
                              className={`text-left px-3 py-2 rounded-lg text-sm transition ${
                                form.includedAirlineCodes.includes(airline.code)
                                  ? 'bg-green-600 text-white font-semibold'
                                  : form.includedAirlineCodes.length >= 3
                                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                  : 'bg-white hover:bg-green-50 border border-green-200'
                              }`}
                            >
                              <span className="font-bold">{airline.code}</span> - {airline.name}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Compagnies leaders en Afrique */}
                      <div className="mb-4">
                        <h3 className="text-xs font-bold text-amber-700 mb-2">
                          🦁 COMPAGNIES LEADERS AFRICAINES
                        </h3>
                        <div className="grid grid-cols-2 gap-2">
                          {africanLeaderAirlines.map((airline) => (
                            <button
                              key={airline.code}
                              type="button"
                              onClick={() => handleAirlineToggle(airline.code)}
                              disabled={
                                !form.includedAirlineCodes.includes(airline.code) &&
                                form.includedAirlineCodes.length >= 3
                              }
                              className={`text-left px-3 py-2 rounded-lg text-sm transition ${
                                form.includedAirlineCodes.includes(airline.code)
                                  ? 'bg-amber-600 text-white font-semibold'
                                  : form.includedAirlineCodes.length >= 3
                                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                  : 'bg-white hover:bg-amber-50 border border-amber-200'
                              }`}
                            >
                              <span className="font-bold">{airline.code}</span> - {airline.name}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Compagnies internationales */}
                      <div>
                        <h3 className="text-xs font-bold text-blue-700 mb-2">
                          ✈️ COMPAGNIES INTERNATIONALES
                        </h3>
                        <div className="grid grid-cols-2 gap-2">
                          {internationalAirlines.map((airline) => (
                            <button
                              key={airline.code}
                              type="button"
                              onClick={() => handleAirlineToggle(airline.code)}
                              disabled={
                                !form.includedAirlineCodes.includes(airline.code) &&
                                form.includedAirlineCodes.length >= 3
                              }
                              className={`text-left px-3 py-2 rounded-lg text-sm transition ${
                                form.includedAirlineCodes.includes(airline.code)
                                  ? 'bg-blue-600 text-white font-semibold'
                                  : form.includedAirlineCodes.length >= 3
                                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                  : 'bg-white hover:bg-blue-50 border'
                              }`}
                            >
                              <span className="font-bold">{airline.code}</span> - {airline.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Compagnies sélectionnées */}
                    {form.includedAirlineCodes.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {form.includedAirlineCodes.map((code) => {
                          // Chercher dans toutes les catégories
                          let airlineName = code;
                          if (code === favoriteAirline.code) {
                            airlineName = favoriteAirline.name;
                          } else {
                            const found = [...westAfricanAirlines, ...africanLeaderAirlines, ...internationalAirlines]
                              .find(a => a.code === code);
                            if (found) airlineName = found.name;
                          }
                          
                          return (
                            <span
                              key={code}
                              className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium"
                            >
                              {airlineName}
                              <button
                                type="button"
                                onClick={() => handleAirlineToggle(code)}
                                className="ml-1 hover:text-red-600"
                              >
                                ×
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Vol direct */}
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      name="nonStop"
                      checked={form.nonStop}
                      onChange={handleChange}
                      className="w-5 h-5 text-blue-500"
                    />
                    <label className="font-medium text-gray-700">Vol direct seulement</label>
                  </div>

                  {/* Devise */}
                  <div>
                    <label className="font-medium text-gray-700">Devise</label>
                    <select
                      name="currencyCode"
                      value={form.currencyCode}
                      onChange={handleChange}
                      className="mt-1 w-full px-3 py-2 border rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    >
                      <option value="XOF">XOF</option>
                      {/*
                        <option value="GNF">GNF</option>
                        <option value="EUR">EUR</option>
                        <option value="USD">USD</option> 
                      */}
                    </select>
                  </div>

                  {/* Bouton recherche */}
                  <button
                    type="submit"
                    className="mt-2 bg-gradient-to-r from-blue-500 to-cyan-400 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition"
                  >
                    🔎 Rechercher
                  </button>
                </motion.form>
              ) : (
                <motion.div
                  key="reduced"
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 50, opacity: 0 }}
                  transition={{ type: "spring", damping: 20 }}
                  className="bg-gradient-to-r from-blue-500 to-cyan-400 text-white rounded-2xl px-8 py-4 text-lg font-semibold flex items-center gap-2 shadow-lg relative"
                >
                  🔎 Recherche en cours...
                  <button
                    type="button"
                    onClick={handleClose}
                    className="ml-auto text-white text-2xl hover:text-red-300"
                  >
                    ×
                  </button>
                </motion.div>
              )}
            </div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default FlightSearchForm;
// ...existing code...